import { X } from "lucide-react-native";
import { FC, useMemo, useState } from "react";

import { Box, Card, HStack, Pressable, Text, VStack } from "@/components/ui";

import InfoTip from "./InfoTip";
import { formatDelta, formatKpi, KPIS } from "./model";
import { regionalSummary } from "./regions";
import RichText from "./RichText";
import { KpiId, Kpis, StateResult } from "./types";
import UsMap from "./UsMap";

/* ── Types ─────────────────────────────────────── */

interface MapPanelProps {
  audienceLabel: string;
  /** State ids covered by the geography filter; others are greyed out. */
  included: Set<string>;
  kpi: KpiId;
  national: Kpis;
  results: StateResult[];
}

interface RankedState {
  delta: number;
  result: StateResult;
  value: number;
}

/* ── Constants ─────────────────────────────────── */

const HIGH = [22, 163, 74]; // green-600: above national
const LOW = [220, 38, 38]; // red-600: below national
const MID = [245, 245, 244]; // neutral: at national
const MUTED = "#e7e5e4"; // outside the geography filter
const RANKED_ROWS = 5;

/* ── Component ─────────────────────────────────── */

const MapPanel: FC<MapPanelProps> = ({
  audienceLabel,
  included,
  kpi,
  national,
  results,
}) => {
  const [pinned, setPinned] = useState<string | null>(null);
  const definition = KPIS.find((k) => k.id === kpi) ?? KPIS[0];

  const ranked = useMemo<RankedState[]>(
    () =>
      results
        .filter((result) => included.has(result.state.id))
        .map((result) => ({
          delta: result.kpis[kpi] - national[kpi],
          result,
          value: result.kpis[kpi],
        }))
        .sort((a, b) => b.value - a.value),
    [included, kpi, national, results]
  );

  const { colors, tooltips } = useMemo(() => {
    const spread = Math.max(
      ...ranked.map((r) => Math.abs(r.delta)),
      definition.format === "currency" ? 0.1 : 2
    );
    const colorMap: Record<string, string> = {};
    const tooltipMap: Record<string, string> = {};
    for (const result of results) {
      if (!included.has(result.state.id)) {
        colorMap[result.state.id] = MUTED;
        tooltipMap[result.state.id] =
          `${result.state.name} · outside the selected region`;
      }
    }
    for (const { delta, result, value } of ranked) {
      colorMap[result.state.id] = shade(delta / spread);
      tooltipMap[result.state.id] =
        `${result.state.name} · ${formatKpi(definition, value)} (${formatDelta(definition, delta)} vs. national)`;
    }
    return { colors: colorMap, tooltips: tooltipMap };
  }, [definition, included, ranked, results]);

  const summary = useMemo(
    () =>
      regionalSummary(
        ranked.map((r) => r.result),
        national,
        kpi,
        definition.label
      ),
    [definition.label, kpi, national, ranked]
  );

  const focus = pinned
    ? ranked.find((r) => r.result.state.id === pinned)
    : undefined;

  return (
    <Card className="p-6 rounded-2xl border border-outline-200">
      <VStack className="gap-4">
        <HStack className="items-start justify-between gap-4">
          <VStack className="gap-0.5">
            <HStack className="items-center gap-1.5">
              <Text className="text-sm font-semibold text-typography-900">
                {definition.label} by state · {audienceLabel}
              </Text>
              <InfoTip
                text={`Each state is shaded by its predicted ${definition.label.toLowerCase()} for ${audienceLabel} relative to the national figure: green = above, red = below. Select another KPI above to re-shade the map.`}
              />
            </HStack>
            <Text className="text-xs text-typography-400">
              National {formatKpi(definition, national[kpi])} · click a state to
              pin its detail
            </Text>
          </VStack>
          <HStack className="items-center gap-2 pt-1 shrink-0">
            <Text className="text-xs text-typography-400">Below</Text>
            <Box className="flex-row h-2 w-32 rounded-full overflow-hidden">
              {Array.from({ length: 20 }, (_, i) => (
                <Box
                  className="flex-1"
                  key={i}
                  style={{ backgroundColor: shade((i - 9.5) / 9.5) }}
                />
              ))}
            </Box>
            <Text className="text-xs text-typography-400">Above</Text>
          </HStack>
        </HStack>

        {summary && (
          <Box
            className="rounded-xl px-4 py-3"
            style={{ backgroundColor: "#faf8ff" }}
          >
            <RichText
              boldClassName="font-semibold text-typography-900"
              className="text-sm leading-[22px] text-typography-700"
              text={summary}
            />
          </Box>
        )}

        <Box className="w-full max-w-[720px] self-center">
          <UsMap
            colors={colors}
            onSelect={(id) =>
              setPinned((current) => (current === id ? null : id))
            }
            selected={pinned}
            tooltips={tooltips}
          />
        </Box>

        <Box className="h-px bg-outline-200" />

        {focus ? (
          <StateDetail
            definition={definition}
            focus={focus}
            onUnpin={
              pinned === focus.result.state.id
                ? () => setPinned(null)
                : undefined
            }
          />
        ) : (
          <HStack className="gap-6">
            <RankedList
              definition={definition}
              rows={ranked.slice(0, RANKED_ROWS)}
              title="Strongest"
            />
            <Box className="w-px bg-outline-200" />
            <RankedList
              definition={definition}
              rows={ranked.slice(-RANKED_ROWS).reverse()}
              title="Weakest"
            />
          </HStack>
        )}
      </VStack>
    </Card>
  );
};

/* ── Subcomponents ─────────────────────────────── */

const RankedList: FC<{
  definition: (typeof KPIS)[number];
  rows: RankedState[];
  title: string;
}> = ({ definition, rows, title }) => (
  <VStack className="flex-1 gap-1.5">
    <Text className="text-xs font-medium uppercase tracking-wide text-typography-500">
      {title}
    </Text>
    {rows.map(({ delta, result, value }) => (
      <HStack className="items-center gap-2" key={result.state.id}>
        <Box
          className="w-2.5 h-2.5 rounded-sm"
          style={{ backgroundColor: shade(Math.sign(delta) * 0.8) }}
        />
        <Text className="flex-1 text-sm text-typography-800" numberOfLines={1}>
          {result.state.name}
        </Text>
        <Text className="text-sm font-semibold text-typography-900">
          {formatKpi(definition, value)}
        </Text>
        <Text
          className={`w-16 text-right text-xs ${delta >= 0 ? "text-success-700" : "text-error-700"}`}
        >
          {formatDelta(definition, delta)}
        </Text>
      </HStack>
    ))}
  </VStack>
);

const StateDetail: FC<{
  definition: (typeof KPIS)[number];
  focus: RankedState;
  onUnpin?: () => void;
}> = ({ definition, focus, onUnpin }) => (
  <VStack className="gap-3">
    <HStack className="items-baseline gap-2">
      <Text className="text-base font-semibold text-typography-900">
        {focus.result.state.name}
      </Text>
      <Text className="text-xl font-bold text-typography-900">
        {formatKpi(definition, focus.value)}
      </Text>
      <Text
        className={`text-xs font-semibold ${focus.delta >= 0 ? "text-success-700" : "text-error-700"}`}
      >
        {formatDelta(definition, focus.delta)} vs. national
      </Text>
      {onUnpin && (
        <Pressable className="p-0.5 self-center" onPress={onUnpin}>
          <X color="rgb(var(--color-typography-500))" size={14} />
        </Pressable>
      )}
    </HStack>

    <HStack className="gap-4 items-start">
      <VStack className="flex-1 gap-1.5">
        <HStack className="items-center gap-1">
          <Text className="text-xs font-medium uppercase tracking-wide text-typography-500">
            Why purchase intent differs
          </Text>
          <InfoTip text="Local market factors that move purchase intent away from the national figure, in percentage points. Top three shown." />
        </HStack>
        {focus.result.reasons.map((reason) => (
          <HStack className="items-start gap-2" key={reason.label}>
            <Box
              className="w-2.5 h-2.5 mt-1.5 rounded-sm"
              style={{
                backgroundColor: shade(Math.sign(reason.delta) * 0.8),
              }}
            />
            <Text className="flex-1 text-sm leading-5 text-typography-800">
              {reason.label}
            </Text>
            <Text
              className={`w-14 pt-0.5 text-right text-xs ${reason.delta >= 0 ? "text-success-700" : "text-error-700"}`}
            >
              {reason.delta > 0 ? "+" : "−"}
              {Math.abs(reason.delta).toFixed(1)}
              {"\u00A0pts"}
            </Text>
          </HStack>
        ))}
      </VStack>

      <Box className="w-px self-stretch bg-outline-200" />

      <VStack className="w-40 gap-1.5">
        <Text className="text-xs font-medium uppercase tracking-wide text-typography-500">
          Other KPIs
        </Text>
        {KPIS.filter((k) => k.id !== definition.id).map((k) => (
          <HStack className="items-center justify-between" key={k.id}>
            <Text className="text-sm text-typography-800">{k.label}</Text>
            <Text className="text-sm font-semibold text-typography-900">
              {formatKpi(k, focus.result.kpis[k.id])}
            </Text>
          </HStack>
        ))}
      </VStack>
    </HStack>
  </VStack>
);

/* ── Helpers ───────────────────────────────────── */

/** t in [-1, 1] (below … above national) → red … neutral … green. */
const shade = (t: number): string => {
  const clamped = Math.max(-1, Math.min(1, t));
  const end = clamped >= 0 ? HIGH : LOW;
  const weight = Math.pow(Math.abs(clamped), 0.8);
  const channel = (i: number) =>
    Math.round(MID[i] + (end[i] - MID[i]) * weight);
  return `rgb(${channel(0)}, ${channel(1)}, ${channel(2)})`;
};

export default MapPanel;
